/* =========================================================================
   ROUND-END XP TALLY (PLAN-progression.md §5.3) — "session end: XP tally
   animation → bar fills". The XP counts up, the bar runs from where it stood
   BEFORE the award to where it stands now (derived from the post-award numbers
   in xpMath.tallyBar), and the promise resolves when the player taps or the
   moment auto-advances.

   CONTRACT (called by gameController at round end — do not change the shape):
     showXpTally({ xpAwarded, xpIntoLevel, xpToNextLevel, level, leveledUp })
   Level-up CELEBRATION is a separate moment (ui/levelUp.ts) — this overlay only
   hints at it so the two can be sequenced: tally → levelUp.
   ========================================================================= */
import { esc } from '../engine';
import { tone } from '../audio';
import { openMoment, prefersReducedMotion, raf } from './momentOverlay';
import { countUpValue, fmtXp, tallyBar } from './xpMath';

/** The hard contract with gameController (Agent B). Do not reshape. */
export interface XpTallyOpts {
  xpAwarded: number;
  xpIntoLevel: number;
  xpToNextLevel: number;
  level: number;
  leveledUp: boolean;
}

const COUNT_MS = 900; // XP roll-up
const FILL_MS = 900; // bar travel (matches the CSS transition)
const HOLD_MS = 1500; // beat to read the result before auto-advance
const GUARD_MS = 450; // a stray tap right at round end must not skip it

/** Show the round-end XP tally. Resolves on dismiss / auto-advance. */
export function showXpTally(opts: XpTallyOpts): Promise<void> {
  const bar = tallyBar(opts);
  const awarded = Math.max(0, Math.round(opts.xpAwarded));
  const into = Math.max(0, Math.round(opts.xpIntoLevel));
  const size = Math.max(0, Math.round(opts.xpToNextLevel));
  const level = Math.max(1, Math.round(opts.level));
  const reduced = prefersReducedMotion();
  const anim = reduced ? 0 : (bar.wraps ? FILL_MS * 2 : FILL_MS) + 260;

  const moment = openMoment({
    id: 'xpTallyOverlay',
    cls: 'xp-ov',
    label: 'Round experience earned',
    guardMs: GUARD_MS,
    autoMs: Math.max(COUNT_MS, anim) + HOLD_MS,
    html:
      `<div class="mo-card xp-card">` +
      `<div class="xp-kicker">Round complete</div>` +
      `<div class="xp-award"><span id="xpAwardNum">+0</span> <span class="xp-award-unit">XP</span></div>` +
      `<div class="xp-meter">` +
      `<span class="xp-level-pill">Level ${esc(level)}</span>` +
      `<span class="xp-bar"><span class="xp-bar-fill" id="xpTallyFill"></span></span>` +
      `</div>` +
      `<div class="xp-into" id="xpTallyInto">${esc(fmtXp(into))}${size > 0 ? ' / ' + esc(fmtXp(size)) : ''} XP</div>` +
      (opts.leveledUp ? `<div class="xp-levelup-hint">Level up</div>` : '') +
      `<div class="xp-close">Tap to continue</div>` +
      `</div>`,
  });

  const fill = moment.root.querySelector<HTMLElement>('#xpTallyFill');
  const num = moment.root.querySelector<HTMLElement>('#xpAwardNum');

  if (reduced) {
    if (fill) fill.style.width = bar.toPct + '%';
    if (num) num.textContent = '+' + fmtXp(awarded);
    return moment.done;
  }

  // Bar: start where the player was, then travel (wrapping through 100% when
  // the award crossed a level boundary).
  if (fill) {
    fill.style.width = bar.fromPct + '%';
    raf(() => {
      if (!bar.wraps) {
        fill.style.width = bar.toPct + '%';
        return;
      }
      fill.style.width = '100%';
      setTimeout(() => {
        fill.classList.add('no-anim'); // snap back to empty, then fill again
        fill.style.width = '0%';
        raf(() => {
          fill.classList.remove('no-anim');
          raf(() => {
            fill.style.width = bar.toPct + '%';
          });
        });
      }, FILL_MS);
    });
  }

  // XP roll-up, with a soft tick every few frames (silent until audio unlocks).
  if (num) {
    const start = Date.now();
    let lastTick = 0;
    const step = (): void => {
      const t = Math.min(1, (Date.now() - start) / COUNT_MS);
      const v = countUpValue(awarded, t);
      num.textContent = '+' + fmtXp(v);
      if (t < 1) {
        if (Date.now() - lastTick > 90) {
          lastTick = Date.now();
          tone(760 + 520 * t, 0.035, 'triangle', 0.02);
        }
        raf(step);
      }
    };
    raf(step);
  }

  return moment.done;
}
